import React, { useState, useEffect } from 'react';

// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';

const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  dialog: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '450px', // Reduced width for success message
    maxWidth: '90vw',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  title: {
    fontSize: '18px',
    textAlign: 'center',
    margin: 0,
    color: '#000',
    backgroundColor: '#CCCCFF',
    padding: '10px',
    borderRadius: '4px'
  }
};

// Copy Requirement Dialog Component
const CopyRaidLogDialog = ({
  isOpen,
  onClose,
  raidLogToCopy,
  corporateAccount,
  currentProject,
  login_token
}) => {
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedRaidType, setSelectedRaidType] = useState('');
  const [copyAttachments, setCopyAttachments] = useState(false);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [availableRaidTypes, setAvailableRaidTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(null); // This will hold the success data
  const [attachmentsCount, setAttachmentsCount] = useState(0);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchStatuses();
      fetchRaidTypes();

      if (raidLogToCopy && raidLogToCopy.raid_id) {
        fetchAttachmentsCount();
      }
    } else {
      // Reset states when dialog closes
      setSelectedStatus('');
      setSelectedRaidType('');
      setCopyAttachments(false);
      setAttachmentsCount(0);
      setError('');
      setCopySuccess(null); // Reset success state
    }
  }, [isOpen, corporateAccount, currentProject, raidLogToCopy]);

  const fetchAttachmentsCount = async () => {
    if (!raidLogToCopy || !raidLogToCopy.raid_id) return;

    setIsLoadingAttachments(true);

    try {
      const response = await fetch(appConfig.getUrl('/get_requirement_attachments'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          req_id: `RAID-${raidLogToCopy.raid_id}`
        })
      });

      const data = await response.json();

      if (data.status === 'Success' && data.attachments) {
        setAttachmentsCount(data.attachments.length);
      } else {
        setAttachmentsCount(0);
      }
    } catch (error) {
      console.error('Error fetching attachments count:', error);
      setAttachmentsCount(0);
    } finally {
      setIsLoadingAttachments(false);
    }
  };

  const fetchStatuses = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_statuses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          entity: 'RAID_STATUS',
          search_query: ''
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        const statuses = data.entity_statuses.map(s => s.status);
        setAvailableStatuses(statuses);
      }
    } catch (error) {
      console.error('Error fetching statuses:', error);
      setError('Error loading statuses: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRaidTypes = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_statuses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          entity: 'RAID_TYPE',
          search_query: ''
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        const raidTypes = data.entity_statuses.map(s => s.status);
        setAvailableRaidTypes(raidTypes);
      }
    } catch (error) {
      console.error('Error fetching raid types:', error);
      setError('Error loading raid types: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(appConfig.getUrl('/copy_raid_log_entry'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          from_project_id: currentProject,
          from_raid_type: raidLogToCopy.raid_type,
          from_raid_id: raidLogToCopy.raid_id,
          to_project_id: currentProject,
          to_raid_type: selectedRaidType,
          to_status: selectedStatus,
          copy_attachments: copyAttachments
        })
      });

      const data = await response.json();

      if (data.status === 'Success') {
        // Set success data instead of showing inline message
        setCopySuccess({
          raidIdWithPrefix: data.raid_id_with_prefix || data.raid_id,
          attachmentsCopied: copyAttachments && attachmentsCount > 0 ? attachmentsCount : 0
        });
      } else {
        setError(data.status_description || 'Failed to copy raid log entry');
      }
    } catch (error) {
      console.error('Error copying raid log entry:', error);
      setError('Error copying raid log entry: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={DialogStyles.overlay}>
      <div style={{
        ...DialogStyles.dialog,
        width: copySuccess ? '500px' : '700px' // Wider form, compact success message
      }}>
        <h2 style={DialogStyles.title}>
          Copy Raid Log Entry
        </h2>

        {copySuccess ? (
          // Success message
          <div>
            <div style={{
              padding: '15px',
              backgroundColor: '#d4edda',
              border: '1px solid #c3e6cb',
              color: '#155724',
              borderRadius: '4px',
              marginBottom: '15px'
            }}>
              <p style={{ fontWeight: '500', margin: '0 0 8px 0' }}>
                RAID Log successfully copied!
              </p>
              <p style={{ margin: '0 0 8px 0' }}>
                New RAID Log ID: <span style={{ fontWeight: 'bold' }}>
                  {copySuccess.raidIdWithPrefix}
                </span>
              </p>
              {copySuccess.attachmentsCopied > 0 && (
                <p style={{ margin: 0 }}>
                  {copySuccess.attachmentsCopied} attachment{copySuccess.attachmentsCopied !== 1 ? 's' : ''} copied
                </p>
              )}
            </div>
            <button
              style={{
                width: '100%',
                padding: '8px 16px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              onClick={() => onClose(true)}
            >
              Close
            </button>
          </div>
        ) : (
          // Form content
          <form onSubmit={(e) => { e.preventDefault(); handleCopy(); }}>
            <div style={{marginBottom: '15px'}}>
              <p style={{display: 'block', marginBottom: '5px', fontSize: '14px', color: '#666'}}>
                You are about to copy the raid log <strong>{raidLogToCopy?.raid_id_with_prefix || `${raidLogToCopy?.raid_type}-${raidLogToCopy?.raid_id}`}</strong>: {raidLogToCopy?.raid_description}
              </p>
            </div>

            <div style={{marginBottom: '15px'}}>
              <label style={{display: 'block', marginBottom: '5px'}}>Target Project:</label>
              <input
                type="text"
                value={currentProject}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: '#f5f5f5'
                }}
                disabled
              />
              <small style={{color: '#dc3545', fontSize: '12px'}}>
                Currently, copying is only supported within the same project
              </small>
            </div>

            <div style={{marginBottom: '15px'}}>
              <label style={{display: 'block', marginBottom: '5px'}}>Target Raid Type*:</label>
              <select
                value={selectedRaidType}
                onChange={(e) => setSelectedRaidType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
                disabled={isLoading}
                required
              >
                <option value="">-- Select Raid Type --</option>
                {availableRaidTypes.length === 0 ? (
                  <option value="" disabled>Loading raid types...</option>
                ) : (
                  availableRaidTypes.map(raidType => (
                    <option key={raidType} value={raidType}>
                      {raidType}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div style={{marginBottom: '15px'}}>
              <label style={{display: 'block', marginBottom: '5px'}}>Target Status*:</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
                disabled={isLoading}
                required
              >
                <option value="">-- Select Status --</option>
                {availableStatuses.length === 0 ? (
                  <option value="" disabled>Loading statuses...</option>
                ) : (
                  availableStatuses.map(status => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Attachments section */}
            {isLoadingAttachments ? (
              <div style={{marginBottom: '15px', display: 'flex', alignItems: 'center'}}>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #3b82f6',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginRight: '8px'
                }}></div>
                <span style={{fontSize: '14px', color: '#666'}}>Checking attachments...</span>
              </div>
            ) : attachmentsCount > 0 ? (
              <div style={{marginBottom: '15px'}}>
                <label style={{display: 'flex', alignItems: 'center', cursor: 'pointer'}}>
                  <input
                    type="checkbox"
                    checked={copyAttachments}
                    onChange={(e) => setCopyAttachments(e.target.checked)}
                    style={{marginRight: '8px'}}
                    disabled={isLoading}
                  />
                  <span style={{fontSize: '14px'}}>
                    Copy attachments ({attachmentsCount} attachment{attachmentsCount !== 1 ? 's' : ''})
                  </span>
                </label>
              </div>
            ) : null}

            {error && (
              <div style={{
                marginBottom: '15px',
                padding: '10px',
                backgroundColor: '#fee2e2',
                border: '1px solid #fca5a5',
                color: '#dc2626',
                borderRadius: '4px'
              }}>
                {error}
              </div>
            )}

            <div style={{display: 'flex', justifyContent: 'center', gap: '16px'}}>
              <button
                type="button"
                onClick={() => onClose(false)}
                style={{
                  backgroundColor: '#FFFDD0',
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                disabled={isLoading}
              >
                Close
              </button>
              <button
                type="submit"
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#C8F4DE',
                  color: 'black',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
                disabled={isLoading || !selectedRaidType || !selectedStatus}
              >
                {isLoading && (
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #000',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginRight: '8px'
                  }}></div>
                )}
                {isLoading ? 'Processing...' : 'Copy Raid Log'}
              </button>
            </div>
          </form>
        )}

        <style jsx>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
};

export default CopyRaidLogDialog;